import http from './http';

/**
 * Active Ringers API (owner directive 2026-10-10).
 *
 * A ring is a live CALL the agent makes on its own initiative, so the dashboard
 * tracks every active ring and lets the user answer / SNOOZE / dismiss it per
 * ringer. Declining a ring SNOOZES it (it comes back) — it is never permanently
 * removed while the agent still wants the user.
 */
export type RingStatus = 'ringing' | 'answered' | 'active' | 'snoozed' | 'dismissed' | 'expired';

export interface ActiveRing {
  id: number;
  title: string;
  body: string | null;
  url: string | null;
  read: boolean;
  created_at: string | null;
  ring_status: RingStatus;
  base_status: string | null;
  is_active: boolean;
  agent: string;
  agent_id: string | null;
  escalation_id: string | null;
  severity: string | null;
  question: string | null;
  ring_count: number;
  last_ring_at: string | null;
  next_ring_at: string | null;
  snooze_until: string | null;
  answered_at: string | null;
}

export interface ActiveRingsResponse {
  rings: ActiveRing[];
  active_count: number;
  total: number;
}

export const ringsApi = {
  list: async (includeSnoozed = true): Promise<ActiveRingsResponse> => {
    const { data } = await http.get<ActiveRingsResponse>('/in-app-notifications/active-rings', {
      params: { include_snoozed: includeSnoozed },
    });
    return data;
  },
  /** Mark a ring answered — it stays tracked/monitored (not removed). */
  answer: async (id: number): Promise<ActiveRing> => {
    const { data } = await http.post<ActiveRing>(`/in-app-notifications/rings/${id}/answer`);
    return data;
  },
  /** Snooze a ring (decline/hold) — it comes back; never permanently removed. */
  snooze: async (id: number, minutes?: number): Promise<ActiveRing> => {
    const { data } = await http.post<ActiveRing>(
      `/in-app-notifications/rings/${id}/snooze`,
      minutes != null ? { minutes } : {},
    );
    return data;
  },
  /** Explicit terminal dismiss of a ring (stop this line). */
  dismiss: async (id: number): Promise<ActiveRing> => {
    const { data } = await http.post<ActiveRing>(`/in-app-notifications/rings/${id}/dismiss`);
    return data;
  },
  /** The END button (owner 2026-10-12): stop ALL calling — dismiss every live
   *  ring (ringing/answered/active) so nothing re-rings after the call ends. */
  endSession: async (): Promise<{ dismissed: number[] }> => {
    const { data } = await http.post<{ dismissed: number[] }>(
      '/in-app-notifications/rings/end-session',
    );
    return data;
  },
};

export default ringsApi;
